import React, { useState, useRef, useCallback } from 'react';
import { analyzeAudioLevel, isLikelyNoiseResult, encodeWAV } from '../utils/audioAnalysis';

const TEST_SCENARIOS = [
  { id: 'speech', label: 'Normal Speech', desc: 'Talk normally into the mic', expectPass: true },
  { id: 'breathing', label: 'Breathing While Talking', desc: 'Breathe audibly while speaking', expectPass: true },
  { id: 'quiet', label: 'Quiet Speech', desc: 'Speak softly, do not yell', expectPass: true },
  { id: 'whisper', label: 'Whisper', desc: 'Whisper into the mic', expectPass: true },
  { id: 'table_bang', label: 'Table Banging', desc: 'Bang on the table 3-4 times', expectPass: false },
  { id: 'clap', label: 'Clapping', desc: 'Clap hands 3-4 times', expectPass: false },
  { id: 'silence', label: 'Silence', desc: 'Do not make any sound', expectPass: false },
  { id: 'static', label: 'Fan / Static Noise', desc: 'Record near a fan or AC unit', expectPass: false },
];

function ResultBadge({ passed, expected }) {
  const correct = passed === expected;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 12,
      fontSize: '0.85rem',
      fontWeight: 600,
      background: correct ? '#16a34a' : '#dc2626',
      color: '#fff',
    }}>
      {correct ? 'CORRECT' : 'WRONG'}
    </span>
  );
}

export default function AudioAnalysisTest() {
  const [isRecording, setIsRecording] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('speech');
  const [results, setResults] = useState([]);
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [predictResult, setPredictResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const audioChunks = useRef([]);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationIdRef = useRef(null);
  const waveformCanvas = useRef(null);
  const processorRef = useRef(null);
  const autoStopRef = useRef(null);
  const intervalRef = useRef(null);
  const [timer, setTimer] = useState(8);

  const startRecording = useCallback(async () => {
    try {
      setPredictResult(null);
      setLiveMetrics(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000
        }
      });

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current.fftSize = 2048;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      function drawWave() {
        const canvas = waveformCanvas.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);
        analyserRef.current.getByteTimeDomainData(dataArrayRef.current);
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#2bffbb';
        const sliceWidth = width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = dataArrayRef.current[i] / 128.0;
          const y = (v * height) / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          x += sliceWidth;
        }
        ctx.stroke();
        animationIdRef.current = requestAnimationFrame(drawWave);
      }
      drawWave();

      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);
      audioChunks.current = [];
      processor.onaudioprocess = (e) => {
        const channelData = e.inputBuffer.getChannelData(0);
        audioChunks.current.push(new Float32Array(channelData));

        // Live metrics
        let sum = 0;
        let maxAbs = 0;
        for (let i = 0; i < channelData.length; i++) {
          const abs = Math.abs(channelData[i]);
          sum += abs;
          if (abs > maxAbs) maxAbs = abs;
        }
        setLiveMetrics({
          avgAmplitude: (sum / channelData.length).toFixed(5),
          peakAmplitude: maxAbs.toFixed(5),
          chunkCount: audioChunks.current.length,
        });
      };

      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(processor);
      processor.connect(audioContextRef.current.destination);
      processorRef.current = processor;

      setIsRecording(true);
      setTimer(8);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => setTimer(t => Math.max(t - 1, 0)), 1000);
      if (autoStopRef.current) clearTimeout(autoStopRef.current);
      autoStopRef.current = setTimeout(() => stopRecording(), 8000);
    } catch (e) {
      alert('Microphone access denied: ' + e.message);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    setIsRecording(false);
    analyzeRecording();
  }, []);

  const analyzeRecording = useCallback(async () => {
    setIsAnalyzing(true);
    try {
      const pcmArrays = audioChunks.current;
      const totalLength = pcmArrays.reduce((sum, arr) => sum + arr.length, 0);
      const merged = new Float32Array(totalLength);
      let offset = 0;
      for (let arr of pcmArrays) {
        merged.set(arr, offset);
        offset += arr.length;
      }

      const wavBlob = encodeWAV(merged, 44100);

      // Client-side analysis
      const analysis = await Promise.race([
        analyzeAudioLevel(wavBlob),
        new Promise(resolve => setTimeout(() => resolve({ isSilent: false, reason: 'timeout' }), 5000))
      ]);

      const scenario = TEST_SCENARIOS.find(s => s.id === selectedScenario);
      const clientPass = !analysis.isSilent;
      const correct = clientPass === scenario.expectPass;

      // Try predict API
      let apiResult = null;
      let apiPass = null;
      let apiCorrect = null;
      try {
        const formData = new FormData();
        formData.append('file', wavBlob, 'test.wav');
        const predictBase = process.env.REACT_APP_PREDICT_URL || '/app';
        const res = await fetch(`${predictBase}/predict`, { method: 'POST', body: formData });
        if (res.status === 422) {
          const errData = await res.json();
          apiResult = errData;
          apiPass = false;
        } else if (res.ok) {
          const data = await res.json();
          apiResult = data;
          apiPass = !isLikelyNoiseResult(data);
        } else {
          apiResult = { error: `HTTP ${res.status}` };
          apiPass = null;
        }
      } catch (e) {
        apiResult = { error: e.message };
        apiPass = null;
      }

      if (apiPass !== null) {
        apiCorrect = apiPass === scenario.expectPass;
      }

      setPredictResult(apiResult);

      setResults(prev => [{
        id: Date.now(),
        scenario: scenario.label,
        expectPass: scenario.expectPass,
        clientPass,
        clientCorrect: correct,
        clientAnalysis: analysis,
        apiPass,
        apiCorrect,
        apiResult,
        timestamp: new Date().toLocaleTimeString(),
      }, ...prev].slice(0, 20));
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedScenario]);

  const correctCount = results.filter(r => r.clientCorrect && (r.apiCorrect !== false)).length;
  const totalCount = results.length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 20, fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ fontSize: '1.8rem', marginBottom: 4 }}>Audio Analysis Test Tool</h1>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Test the voice detection system with different audio scenarios.
        Record audio and see if the client-side analysis and predict API correctly detect speech vs non-speech.
      </p>

      {/* Scenario selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>Select Test Scenario:</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
          {TEST_SCENARIOS.map(s => (
            <button
              key={s.id}
              onClick={() => setSelectedScenario(s.id)}
              style={{
                padding: '10px 12px',
                border: selectedScenario === s.id ? '2px solid #2bffbb' : '2px solid #ddd',
                borderRadius: 8,
                background: selectedScenario === s.id ? '#f0fdf4' : '#fff',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.label}</div>
              <div style={{ fontSize: '0.8rem', color: '#666' }}>{s.desc}</div>
              <div style={{ fontSize: '0.75rem', color: s.expectPass ? '#16a34a' : '#dc2626', marginTop: 4 }}>
                Expected: {s.expectPass ? 'PASS (speech)' : 'REJECT (noise)'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Record button + waveform */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isAnalyzing}
          style={{
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 8,
            border: 'none',
            background: isRecording ? '#dc2626' : '#2bffbb',
            color: isRecording ? '#fff' : '#000',
            cursor: isAnalyzing ? 'wait' : 'pointer',
            minWidth: 160,
          }}
        >
          {isRecording ? `Stop (${timer}s)` : isAnalyzing ? 'Analyzing...' : 'Record 8s'}
        </button>
        {liveMetrics && (
          <div style={{ fontSize: '0.85rem', color: '#555' }}>
            <span>Avg: {liveMetrics.avgAmplitude}</span>
            {' | '}
            <span>Peak: {liveMetrics.peakAmplitude}</span>
            {' | '}
            <span>Chunks: {liveMetrics.chunkCount}</span>
          </div>
        )}
      </div>

      <canvas
        ref={waveformCanvas}
        width={860}
        height={80}
        style={{
          display: isRecording ? 'block' : 'none',
          background: '#1a1a2e',
          borderRadius: 8,
          marginBottom: 16,
          width: '100%',
        }}
      />

      {/* API result */}
      {predictResult && (
        <div style={{ padding: 16, background: '#f8f8f8', borderRadius: 8, marginBottom: 16, fontSize: '0.85rem' }}>
          <strong>Predict API Response:</strong>
          <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
            {JSON.stringify(predictResult, null, 2)}
          </pre>
        </div>
      )}

      {/* Score */}
      {totalCount > 0 && (
        <div style={{ padding: 12, background: '#f0f9ff', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}>
          Score: {correctCount}/{totalCount} correct ({Math.round(correctCount / totalCount * 100)}%)
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div>
          <h3 style={{ marginBottom: 8 }}>Test Results</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>Time</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Scenario</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>Expected</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>Client</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>Client OK</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>API</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>API OK</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 8 }}>{r.timestamp}</td>
                    <td style={{ padding: 8 }}>{r.scenario}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {r.expectPass ? '✅ Pass' : '❌ Reject'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {r.clientPass ? 'Pass' : 'Reject'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <ResultBadge passed={r.clientPass} expected={r.expectPass} />
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {r.apiPass === null ? 'Error' : r.apiPass ? 'Pass' : 'Reject'}
                    </td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {r.apiCorrect === null ? '—' : <ResultBadge passed={r.apiPass} expected={r.expectPass} />}
                    </td>
                    <td style={{ padding: 8, fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.clientAnalysis.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Clear button */}
      {results.length > 0 && (
        <button
          onClick={() => setResults([])}
          style={{ marginTop: 16, padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
        >
          Clear Results
        </button>
      )}
    </div>
  );
}
